import React, { useEffect, useState } from 'react';
import './slideout.tray.css';
import ChatInterface, { ChatDisplayConfig } from '../../modules/ai/chat/ChatInterface';
import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface SlideOutTrayProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TrayConfig {
  displayConfig?: ChatDisplayConfig;
  source?: string;
  threadId?: string;
}

const SlideOutTray: React.FC<SlideOutTrayProps> = ({ isOpen, onClose }) => {
  const [trayConfig, setTrayConfig] = useState<TrayConfig>({});
  
  // Listen for openAIChat events that may include display configuration
  useEffect(() => {
    const handleOpenAIChat = (event: CustomEvent) => {
      console.log('🎨 [SlideOutTray] Received openAIChat event:', event.detail);
      
      const detail = event.detail || {};
      setTrayConfig({
        displayConfig: detail.displayConfig,
        source: detail.source,
        threadId: detail.threadId,
      });
    };
    
    window.addEventListener('openAIChat', handleOpenAIChat as EventListener);
    
    return () => {
      window.removeEventListener('openAIChat', handleOpenAIChat as EventListener);
    };
  }, []);
  
  // Reset config when tray closes
  useEffect(() => {
    if (!isOpen) {
      // Small delay before resetting to avoid flicker
      const timer = setTimeout(() => {
        setTrayConfig({});
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // Check if opened from insight card - we apply styling but NOT insightMode
  // This way the ChatInterface keeps its existing messages loaded from server
  const fromInsightCard = trayConfig.source === 'insight-card';
  
  // Build custom header for insight card view (read-only viewing of insight)
  const insightHeader = fromInsightCard ? (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 2,
      borderBottom: '1px solid rgba(255,255,255,0.1)',
      backgroundColor: 'rgba(0,0,0,0.3)',
    }}>
      <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
        AI Insight Details
      </Typography>
      <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
        <CloseIcon />
      </IconButton>
    </Box>
  ) : undefined;
  
  // Build display config based on source
  // When from insight-card: hide prompt/toolbar for read-only view, but DON'T use insightMode
  // This keeps the existing loaded messages intact
  const effectiveDisplayConfig: ChatDisplayConfig | undefined = fromInsightCard ? {
    showPromptSection: false,
    showToolbar: false,
    showVoiceControls: false,
    customHeader: insightHeader,
    backgroundColor: '#000000',
    variant: 'insight',
    readOnly: true,
    ...trayConfig.displayConfig, // Allow overrides from event
  } : trayConfig.displayConfig;

  return (
    <div className={`slide-out-tray ${isOpen ? 'open' : ''}`}>
      <Box
        sx={{
          height: '100%',
          p: fromInsightCard ? 0 : 2, // No padding for insight view for full-bleed header
          borderRadius: 3,
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          background: fromInsightCard 
            ? '#000000' 
            : 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.4)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 
          IMPORTANT: We do NOT pass insightMode here!
          The ChatInterface already has messages loaded from the server.
          We just apply styling via displayConfig to hide prompt/toolbar.
        */}
        <ChatInterface 
          displayConfig={effectiveDisplayConfig}
        />
      </Box>
    </div>
  );
};

export default SlideOutTray;